import { Card, Box } from "@mui/material";
import classNames from "classnames";
import React from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { useSnapshot } from "valtio";

import { designStore, generalConfigStore, setSelectedCategory } from "@store";

import { data } from "@pages/GeneralConfig/Design/Content/OrderTaking/fastFoodCard";

import { resolveCategoryColor } from "@helpers/categoryColor";

import {
    CATEGORY_BORDER_STYLE_SVG,
    CATEGORY_BORDER_STYLE_UNI,
    CATEGORY_DISPLAY_MODE_2,
    CATEGORY_OPACITY_MAX,
    CATEGORY_SVG_BORDER_WIDTH,
    CATEGORY_SVG_BORDER_SLICE,
    CATEGORY_UNI_BORDER_WIDTH,
    CATEGORY_CARD_BORDER_RADIUS,
} from "../../../../../../constants";

import "./category.css";
import "swiper/swiper.min.css";

const DEFAULT_CATEGORY_TEXT_COLOR = "#343434";

export function hexToRgba(hex: string, opacityPercent: number): string {
    const sanitized = hex.replace("#", "");

    // Garde : si ce n'est pas un hex 3/6 chiffres (ex. déjà "rgb(...)",
    // "transparent" ou valeur vide), on renvoie la valeur telle quelle pour
    // éviter un rgba(NaN, ...) invalide.
    if (!/^([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(sanitized)) {
        return hex;
    }

    const full =
        sanitized.length === 3
            ? sanitized
                  .split("")
                  .map((c) => c + c)
                  .join("")
            : sanitized;

    const r = parseInt(full.substring(0, 2), 16);
    const g = parseInt(full.substring(2, 4), 16);
    const b = parseInt(full.substring(4, 6), 16);

    const alpha =
        Math.max(0, Math.min(100, opacityPercent)) / CATEGORY_OPACITY_MAX;

    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export function CategoriesSwiperWrapper(): JSX.Element {
    const globalCategories = data.categories;

    const {
        isOrderTakingCategoryTitleActive,
        isOrderTakingTopBannerActive,
        project,
    } = useSnapshot(generalConfigStore);

    const {
        selectedCategory,
        categoryDisplayMode,
        categoryDisplayBackgroundColor,
        categoryDisplayBackgroundOpacity,
        categoryDisplaySelectedTextColor,
        categoryDisplayBorderStyle,
        categoryDisplayBorderColor,
        categoryDisplaySvgBorder,
    } = useSnapshot(designStore);

    const generalDesign = project.template.content.generalDesign;

    const resolvePrimary = (color: string): string =>
        resolveCategoryColor(generalDesign, color);

    const isMode2 = categoryDisplayMode === CATEGORY_DISPLAY_MODE_2;

    React.useEffect(() => {
        if (globalCategories.length) {
            setSelectedCategory(globalCategories[0]);
        }
    }, [globalCategories]);

    return (
        <Swiper
            style={{
                height: [isOrderTakingTopBannerActive].some((v) => v === false)
                    ? "454px"
                    : "400px",

                marginTop: [isOrderTakingTopBannerActive].some(
                    (v) => v === false
                )
                    ? "120px"
                    : "50px",
            }}
            className="category-swiper__clz"
            spaceBetween={1}
            speed={500}
            grabCursor
            centeredSlides
            slidesPerView={5}
            loop
            direction="vertical"
            centeredSlidesBounds
            autoplay={{
                delay: 300,
                disableOnInteraction: false,
                reverseDirection: false,
            }}
            slideToClickedSlide
            onSlideChangeTransitionStart={(swiper) => swiper.autoplay.stop()}
        >
            {globalCategories.map((category) => {
                const isActive = category.name === selectedCategory?.name;
                return (
                    // category.name est l'identité stable (unique dans
                    // data.categories) déjà utilisée pour l'état actif ; évite
                    // que React/Swiper (loop=true clone les slides) réconcilie
                    // sur la position et colle l'état au mauvais slide.
                    <SwiperSlide key={category.name}>
                        <Box
                            className="d-flex flex-column justify-content-center align-items-center"
                            style={{
                                gap: isOrderTakingCategoryTitleActive
                                    ? "5px"
                                    : undefined,
                                cursor: "pointer",
                            }}
                        >
                            {/* ================= WRAPPER ================= */}
                            <Box
                                onClick={() => setSelectedCategory(category)}
                                sx={{
                                    position: "relative",
                                    width: "57px",
                                    height: "56px",
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                }}
                            >
                                {/* ================= SVG BORDER ================= */}
                                {/* Le SVG est dessiné uniquement sur le contour
                                    de la carte via border-image (pas en fond). */}
                                {isActive &&
                                    categoryDisplayBorderStyle ===
                                        CATEGORY_BORDER_STYLE_SVG &&
                                    categoryDisplaySvgBorder && (
                                        <Box
                                            sx={{
                                                position: "absolute",
                                                inset: 0,
                                                borderStyle: "solid",
                                                borderWidth: `${CATEGORY_SVG_BORDER_WIDTH}px`,
                                                borderImage: `url(${categoryDisplaySvgBorder}) ${CATEGORY_SVG_BORDER_SLICE} stretch`,
                                                pointerEvents: "none",
                                                zIndex: 3,
                                            }}
                                        />
                                    )}

                                {/* ================= UNI BORDER ================= */}
                                {/* Au-dessus de la carte (zIndex 2) pour que la
                                    bordure reste visible par-dessus le fond. */}
                                {isActive &&
                                    categoryDisplayBorderStyle ===
                                        CATEGORY_BORDER_STYLE_UNI && (
                                        <Box
                                            sx={{
                                                position: "absolute",
                                                inset: 0,
                                                border: `${CATEGORY_UNI_BORDER_WIDTH} solid ${resolvePrimary(
                                                    categoryDisplayBorderColor
                                                )}`,
                                                borderRadius: CATEGORY_CARD_BORDER_RADIUS,
                                                pointerEvents: "none",
                                                zIndex: 3,
                                            }}
                                        />
                                    )}

                                {/* ================= CARD ================= */}
                                <Card
                                    className={classNames({
                                        "category-card__clz":
                                            !isActive || isMode2,
                                        "active-category-card__clz":
                                            isActive && !isMode2,
                                    })}
                                    sx={{
                                        width: "57px",
                                        height: "56px",
                                        borderRadius: CATEGORY_CARD_BORDER_RADIUS,
                                        boxShadow: "0px 3px 6px #00000029",
                                        background:
                                            isMode2 && isActive
                                                ? hexToRgba(
                                                      resolvePrimary(
                                                          categoryDisplayBackgroundColor
                                                      ),
                                                      categoryDisplayBackgroundOpacity
                                                  )
                                                : "transparent",
                                        zIndex: 2,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: "100%",
                                            height: "100%",
                                            display: "flex",
                                            justifyContent: "center",
                                            alignItems: "center",
                                        }}
                                    >
                                        <img
                                            src={category.image}
                                            alt={category.name}
                                            style={{
                                                height: "43px",
                                            }}
                                        />
                                    </Box>
                                </Card>
                            </Box>

                            {/* ================= TITLE ================= */}
                            {isOrderTakingCategoryTitleActive && (
                                <Box
                                    sx={{
                                        font:
                                            "normal normal bold 10px/15px Segoe UI",
                                        textAlign: "center",
                                        color:
                                            isMode2 && isActive
                                                ? resolvePrimary(
                                                      categoryDisplaySelectedTextColor
                                                  )
                                                : DEFAULT_CATEGORY_TEXT_COLOR,
                                        textTransform: "uppercase",
                                    }}
                                >
                                    {category.name}
                                </Box>
                            )}
                        </Box>
                    </SwiperSlide>
                );
            })}
        </Swiper>
    );
}
